import React, { useEffect, useMemo, useRef } from "react";
import { shallowEqual, useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import { useFacilityBarns } from "components/BarnNumberPicker/useFacilityBarns";
import ErrorBoundary from "components/ErrorBoundary";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { examsRowDataMapper } from "components/VetExamsTable/helper";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useDefaultTab } from "hooks/useDefaultTab";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import {
  setFacilityBarns,
  setUpcomingRaceEntryListRequestKey
} from "store/actions/preRaceScreeningPage";
import { getStringMatchQueryField } from "utils/required-values-utils";
import PreRaceScreeningPageHeader from "./PreRaceScreeningPageHeader";
import PreRaceScreeningSubHeader from "./PreRaceScreeningSubHeader";
import PreRacesTable from "./PreRacesTable";
import {
  checkCurrentRaceEntriesTableTab,
  getDateRangeFilter,
  getSortingRules
} from "./helper";
import {
  commonRequestedFields,
  INFINITE_LIST_UPCOMING_RACE_ENTRIES_QUERY_KEY,
  possiblePreRaceScreeningRoutes
} from "./options";
import { columnSortingOptions } from "./types";

import useStyles from "./styles";

const PreRaceScreeningPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const { currentUser } = useLoggedInUser();

  const {
    selectedTab,
    selectedFacility,
    showPassedEntries,
    orderBy,
    order,
    searchValue,
    showAssignedToMeOnly,
    selectedDateRange,
    isInfiniteScrollOn
  } = useAppSelector(
    state => ({
      selectedTab: state.preRaceScreeningPage.selectedTab,
      selectedFacility: state.preRaceScreeningPage.selectedFacility,
      showPassedEntries: state.preRaceScreeningPage.showPassedEntries,
      orderBy: state.preRaceScreeningPage.orderBy,
      order: state.preRaceScreeningPage.order,
      searchValue: state.preRaceScreeningPage.searchValue,
      showAssignedToMeOnly: state.preRaceScreeningPage.showAssignedToMeOnly,
      selectedDateRange: state.preRaceScreeningPage.selectedDateRange,
      isInfiniteScrollOn: state.queriesConfig.isInfiniteScrollOn
    }),
    shallowEqual
  );

  const { facilityBarns, isLoading: isListBarnsLoading } = useFacilityBarns(
    selectedFacility?.id
  );
  const selectedFacilityId = selectedFacility?.id;
  const hasSelectedFacility = Boolean(selectedFacilityId);

  const { useInfiniteListUpcomingRaceEntries } = useRacehorse360Api();

  useDefaultTab(possiblePreRaceScreeningRoutes, null, selectedTab.path);

  const {
    isEntryExamsTab,
    isEntryResultsTab,
    isRaceDayTab,
    isRaceDayResultsTab
  } = checkCurrentRaceEntriesTableTab(selectedTab.value);
  const shouldFilterPassedEntries =
    showPassedEntries &&
    (isEntryResultsTab || isRaceDayTab || isRaceDayResultsTab);
  const shouldFilterByEntryExamAssignee =
    isEntryExamsTab && showAssignedToMeOnly;
  const shouldFilterByRaceDayExamAssignee =
    isRaceDayTab && showAssignedToMeOnly;

  const upcomingRaceEntriesQuery = useMemo(
    (): Partial<racehorse360.UpcomingRaceEntryFilter> => ({
      facilityIds: selectedFacilityId ? [selectedFacilityId] : null,
      statuses: selectedTab.entryStatuses,
      raceDate: getDateRangeFilter(
        isRaceDayTab || isRaceDayResultsTab,
        selectedDateRange
      ),
      entryExamResult:
        (isEntryResultsTab || isRaceDayTab) && shouldFilterPassedEntries
          ? racehorse360.HorseExamResult.HORSE_EXAM_RESULT_PASSED
          : null,
      raceDayExamResult:
        isRaceDayResultsTab && shouldFilterPassedEntries
          ? racehorse360.HorseExamResult.HORSE_EXAM_RESULT_PASSED
          : null,
      horseOrTrainerName: getStringMatchQueryField("contains", searchValue),
      entryExamAssignedUserId: shouldFilterByEntryExamAssignee
        ? currentUser.rh360Id
        : null,
      raceDayExamAssignedUserId: shouldFilterByRaceDayExamAssignee
        ? currentUser.rh360Id
        : null
    }),
    [
      selectedFacilityId,
      selectedTab.entryStatuses,
      isRaceDayTab,
      shouldFilterPassedEntries,
      searchValue,
      showAssignedToMeOnly,
      selectedDateRange
    ]
  );

  const {
    data: upcomingRaceEntriesData,
    isLoading: isListUpcomingEntriesLoading,
    isFetching,
    isRefetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    compoundRequestKey
  } = useInfiniteListUpcomingRaceEntries(
    INFINITE_LIST_UPCOMING_RACE_ENTRIES_QUERY_KEY,
    {
      query: upcomingRaceEntriesQuery,
      pagingOptions: {
        maxResults: selectedTab.itemsPerPage
      },
      getOptions: {
        select: [
          ...commonRequestedFields,
          ...selectedTab.additionalRequestedFields
        ],
        orderBy: getSortingRules(columnSortingOptions.get(orderBy), order)
      }
    },
    {
      enabled: hasSelectedFacility
    }
  );

  const preparedTableRows = upcomingRaceEntriesData?.pages
    ?.flat()
    .map(item => examsRowDataMapper(item, selectedTab.value));

  useInfiniteScroll(
    tableBodyRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInfiniteScrollOn,
    true
  );

  const shouldShowMainLoader = [
    isListBarnsLoading,
    isListUpcomingEntriesLoading,
    !preparedTableRows,
    isFetching && !isFetchingNextPage && !isRefetching
  ].some(Boolean);

  const renderContent = () => {
    if (shouldShowMainLoader) return <Loader />;

    return (
      <ErrorBoundary>
        <PreRacesTable
          tableRows={preparedTableRows}
          tableBodyRef={tableBodyRef}
          isFetchingNextPage={isFetchingNextPage}
          isRefetching={isRefetching}
        />
      </ErrorBoundary>
    );
  };

  useEffect(() => {
    dispatch(setFacilityBarns(facilityBarns));
  }, [facilityBarns]);

  useEffect(() => {
    dispatch(setUpcomingRaceEntryListRequestKey(compoundRequestKey));
  }, [compoundRequestKey]);

  return (
    <AppPage>
      <AppPageHeader className={classes.appPageHeader}>
        <PreRaceScreeningPageHeader
          upcomingRaceEntriesQuery={upcomingRaceEntriesQuery}
          tableRows={preparedTableRows}
        />
        <PreRaceScreeningSubHeader
          upcomingRaceEntriesQuery={upcomingRaceEntriesQuery}
        />
      </AppPageHeader>
      <AppPageContent>{renderContent()}</AppPageContent>
    </AppPage>
  );
};

export default React.memo(PreRaceScreeningPage);
